// =========================================================================
// Source drawer — the demo's `audit` / `openDoc` drawer. Fork-owned (zone C).
//
// Two shapes, one drawer kind: a slice of a connected system's table, or a
// document out of the data lake. Both carry provenance, because the whole
// point of the surface is "where did this number come from".
//
// The table shape is the demo's `audit()` (line 1907–1911) in full:
//   - an optional "How this figure is calculated" box above the table, for
//     the figures whose click carried a `calc` note;
//   - a "Filter rows…" box that narrows the rows as you type;
//   - highlighted rows, when the opener marked some;
//   - a 400-row render cap, with the demo's "Showing 400 of N" note.
// The payload therefore carries every matched row, not the first 400 — the
// filter box has to be able to find row 401. The filter and cap logic is the
// sibling `SourceDrawerBody.transform.ts`.
//
// The body does NOT render `payload.title`: `chrome/Drawer.tsx` already prints
// it in the drawer header, and every caller passes the same string to both.
// Openers must therefore pass `title` on the drawer props, not rely on the
// body to show it.
// =========================================================================

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  SOURCE_ROW_LIMIT,
  formatCell,
  rowHaystacks,
  visibleRows,
} from './SourceDrawerBody.transform';

/**
 * "How this figure is calculated" — the demo's `opts.calc` (line 1909).
 * `formula` is the rule in words; `steps` are the same rule with the figures
 * the scene showed substituted in, so the reader can check the arithmetic.
 */
export type SourceCalc = {
  formula: string;
  steps?: readonly string[];
};

/** A slice of rows from a source system, with where it came from. */
export type SourceTablePayload = {
  kind: 'table';
  title: string;
  columns: string[];
  /**
   * Row-major; cell order matches `columns`. Every matched row — the body
   * caps what it renders at 400, not the payload.
   */
  rows: (string | number | null)[][];
  /** e.g. "meridian-data/02_sap_pm/work_orders.csv · CSV · 3 of 3,920 rows · filter: wo_id=WO-26-11842". */
  provenance: string;
  /** Indexes into `rows` to highlight — the demo's `opts.hl`, resolved by the opener. */
  highlight?: readonly number[];
  calc?: SourceCalc;
};

/** A file out of the data lake. */
export type SourceDocPayload = {
  kind: 'doc';
  title: string;
  /** Lake path, e.g. "lake/service/bulletins/SB-2026-014.md". */
  path: string;
  /** Connector id that supplied it, e.g. "sharepoint-service". */
  conn: string;
  text: string;
};

export type SourcePayload = SourceTablePayload | SourceDocPayload;

const LABEL_STYLE = {
  color: 'var(--ink-tertiary)',
  fontSize: 10,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  fontWeight: 600,
};

export function SourceDrawerBody({ data }: { data: Partial<SourcePayload> }) {
  if (!data || !data.kind) {
    return (
      <div className="flex-1 p-5">
        <p style={{ color: 'var(--ink-secondary)', fontSize: 13 }}>
          No source attached to this citation.
        </p>
      </div>
    );
  }

  if (data.kind === 'doc') {
    const doc = data as SourceDocPayload;
    return (
      <div className="scroll-soft flex-1 overflow-y-auto p-5">
        <div style={LABEL_STYLE}>Document</div>
        <p className="mono mt-1" style={{ color: 'var(--ink-tertiary)', fontSize: 11 }}>
          {doc.path}
        </p>
        <pre
          className="mt-4 whitespace-pre-wrap rounded-lg p-3"
          style={{
            background: 'var(--surface-1)',
            border: '1px solid var(--hairline)',
            color: 'var(--ink-secondary)',
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          {doc.text}
        </pre>
        <p className="mt-4" style={{ color: 'var(--ink-tertiary)', fontSize: 11 }}>
          Connector · <span className="mono">{doc.conn}</span>
        </p>
      </div>
    );
  }

  const table = data as SourceTablePayload;
  // Keyed so a second drawer opened on top starts with an empty filter box.
  return <SourceTableBody key={`${table.title}|${table.provenance}`} table={table} />;
}

function SourceTableBody({ table }: { table: SourceTablePayload }) {
  const [query, setQuery] = useState('');
  const haystacks = useMemo(() => rowHaystacks(table.rows), [table.rows]);
  const highlight = useMemo(() => new Set(table.highlight ?? []), [table.highlight]);
  const view = useMemo(
    () => visibleRows(table.rows, haystacks, query, highlight),
    [table.rows, haystacks, query, highlight],
  );

  return (
    <div className="scroll-soft flex-1 overflow-y-auto p-5">
      {table.calc ? <CalcBox calc={table.calc} /> : null}

      <div className="flex items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
            style={{ color: 'var(--ink-tertiary)' }}
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter rows…"
            aria-label="Filter rows"
            autoComplete="off"
            className="w-full rounded-lg border py-1.5 pl-8 pr-2 text-[12px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
            style={{
              borderColor: 'var(--hairline)',
              background: 'var(--surface-1)',
              color: 'var(--ink-primary)',
            }}
          />
        </div>
        <span className="flex-shrink-0" style={{ color: 'var(--ink-tertiary)', fontSize: 11 }}>
          {table.columns.length} columns
        </span>
      </div>

      <div
        className="mt-3 overflow-x-auto rounded-lg"
        style={{ border: '1px solid var(--hairline)' }}
      >
        <table className="w-full" style={{ fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {table.columns.map((col) => (
                <th
                  key={col}
                  scope="col"
                  className="px-3 py-2 text-left"
                  style={{
                    color: 'var(--ink-tertiary)',
                    borderBottom: '1px solid var(--hairline)',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.rows.map((row, i) => (
              <tr
                key={row.index}
                aria-current={row.highlighted ? 'true' : undefined}
                style={row.highlighted ? { background: 'var(--accent-soft)' } : undefined}
              >
                {row.cells.map((cell, j) => (
                  <td
                    key={j}
                    className={typeof cell === 'number' ? 'mono px-3 py-2 text-right' : 'px-3 py-2'}
                    style={{
                      color: row.highlighted ? 'var(--ink-primary)' : 'var(--ink-secondary)',
                      borderTop: i === 0 ? undefined : '1px solid var(--hairline)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatCell(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {view.matched === 0 ? (
          <p className="px-3 py-3" style={{ color: 'var(--ink-tertiary)', fontSize: 12 }}>
            {query ? 'No rows match that filter.' : 'No rows.'}
          </p>
        ) : null}
      </div>

      {view.capped ? (
        <p className="mt-2" style={{ color: 'var(--ink-tertiary)', fontSize: 11 }}>
          {`Showing ${SOURCE_ROW_LIMIT} of ${view.matched.toLocaleString('en-AU')}. Full file in the data pack.`}
        </p>
      ) : null}

      <p className="mt-3" style={{ color: 'var(--ink-tertiary)', fontSize: 11 }}>
        {table.provenance}
      </p>
    </div>
  );
}

/** Demo line 1909. The calculation note, above the rows it was worked from. */
function CalcBox({ calc }: { calc: SourceCalc }) {
  return (
    <section
      aria-label="How this figure is calculated"
      className="mb-4 rounded-lg p-3"
      style={{
        background: 'var(--accent-soft)',
        border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)',
      }}
    >
      <div style={LABEL_STYLE}>How this figure is calculated</div>
      <p className="mt-1.5" style={{ color: 'var(--ink-primary)', fontSize: 12.5, lineHeight: 1.55 }}>
        {calc.formula}
      </p>
      {calc.steps && calc.steps.length ? (
        <ol className="mono mt-2 space-y-0.5" style={{ color: 'var(--ink-secondary)', fontSize: 11.5 }}>
          {calc.steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}

export default SourceDrawerBody;
